import _ from 'lodash'

import i18n from '@/i18n/i18n'
import { GoalService } from '@/services/admin/GoalService'

import { errorMessage } from '@/utilities/helpers.js'

import { RETAILER_GOAL_ERROR_MESSAGES } from '@/constants/errorMessages'

const { t } = i18n.global

export const namespaced = true

export const state = {
  goals: [],
  goal: {},
  retailers: null,
  retailerMeta: null,
  rankId: null,
  requirementMeta: null,
  requirements: null
}

export const mutations = {
  SET_GOALS(state, goals) {
    state.goals = goals
  },
  SET_GOAL(state, goal) {
    state.goal = goal
    let index = _.findIndex(state.goals, item => item.id === goal.id)
    if (index === -1) {
      return
    }
    state.goals[index] = goal
  },
  ADD_GOAL(state, goal) {
    state.goals.push(goal)
  },
  DELETE_GOAL(state, goalId) {
    state.goals = state.goals?.filter(goal => goal.id !== goalId)
  },
  SET_RANK_ID(state, rankId) {
    state.rankId = rankId
  },
  SET_RETAILERS(state, retailers) {
    state.retailers = retailers.data
    state.retailerMeta = retailers.meta
  },
  SET_REQUIREMENTS(state, requirements) {
    state.requirements = requirements.data
    state.requirementMeta = requirements.meta
  },
  ADD_REQUIREMENT(state, requirement) {
    state.requirements.push(requirement)
  },
  DELETE_REQUIREMENT(state, requirementId) {
    state.requirements = state.requirements.filter(requirement => requirement.id !== requirementId)
  }
}

export const getters = {
  getGoalById: state => id => {
    return state.goals ? state.goals.find(goal => goal.id === id) : false
  }
}

export const actions = {
  getGoals({ commit, dispatch, state }, params) {
    if (state.goals && state.rankId === params.rankId && !params.force) {
      return state.goals
    }

    return new Promise((resolve, reject) => {
      GoalService.getGoals(params.rankId)
        .then(response => {
          commit('SET_GOALS', response.data)
          commit('SET_RANK_ID', params.rankId)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_GOAL_ERROR_MESSAGES.getGoalsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getGoal({ commit, state, getters }, params) {
    if (params.goalId === state.goal.id) return state.goal

    let goal = getters.getGoalById(params.goalId)

    if (goal) {
      commit('SET_GOAL', goal)
      return goal
    } else {
      return new Promise(resolve => {
        GoalService.getGoal(params.rankId, params.goalId).then(response => {
          commit('SET_GOAL', response.data)
          resolve(response.data)
        })
      })
    }
  },
  createGoal({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      GoalService.createGoal(params.rankId, params.goal)
        .then(response => {
          commit('ADD_GOAL', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateGoal({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      GoalService.updateGoal(params.rankId, params.goalId, params.goal)
        .then(response => {
          commit('SET_GOAL', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.update_error', {
                element: t('store.admin.goals.goal')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteGoal({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      GoalService.deleteGoal(params.rank_id, params.goal_id)
        .then(response => {
          commit('DELETE_GOAL', params.goal_id)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.goals.goal')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRetailers({ commit, dispatch, state }, params) {
    if (state.retailers && state.goalId === params.goalId) return state.retailers

    return new Promise((resolve, reject) => {
      GoalService.getRetailers(params.goalId, params.query)
        .then(response => {
          commit('SET_RETAILERS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.goals.retailers_that_the_goal_defined')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },

  getRequirements({ commit, dispatch, state }, goalId) {
    return new Promise((resolve, reject) => {
      GoalService.getRequirements(goalId)
        .then(response => {
          commit('SET_REQUIREMENTS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.goals.goal_requirements')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createRequirement({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      GoalService.createRequirement(params.goalId, params.requirement)
        .then(response => {
          commit('ADD_REQUIREMENT', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.create_error', {
                element: t('store.admin.goals.goal_requirements')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteRequirement({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      GoalService.deleteRequirement(params.goal_id, params.requirement_id)
        .then(response => {
          commit('DELETE_REQUIREMENT', params.requirement_id)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.goals.goal_requirements')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
